'use client';

import * as React from 'react';
import { DateTimePicker as MantineDateTimePicker } from '@mantine/dates';
import { useTheme } from '@/contexts/ThemeContext';
import { Label } from './label';
import { cn } from '@/lib/utils';

interface DateTimePickerProps {
  value?: Date | undefined;
  onChange?: (date: Date | undefined) => void;
  placeholder?: string;
  datePlaceholder?: string;
  timePlaceholder?: string;
  disabled?: boolean;
  fromDate?: Date;
  toDate?: Date;
  className?: string;
  label?: string;
  required?: boolean;
  error?: string;
  // Time interval in minutes (e.g., 15, 30, 60) - used for time picker step
  timeStep?: number;
  // Minimum time (HH:MM format)
  minTime?: string;
  // Maximum time (HH:MM format)
  maxTime?: string;
}

/**
 * DateTimePicker component using Mantine's built-in DateTimePicker
 * This is the gold standard component from the Mantine framework that's already in use.
 * It provides a unified date and time selection experience.
 */
// eslint-disable-next-line max-lines-per-function
export function DateTimePicker({
  value,
  onChange,
  placeholder = 'Select date and time',
  // eslint-disable-next-line no-unused-vars
  datePlaceholder: _datePlaceholder,
  // eslint-disable-next-line no-unused-vars
  timePlaceholder: _timePlaceholder,
  disabled = false,
  fromDate,
  toDate,
  className,
  label,
  required = false,
  error,
  timeStep = 30,
  // eslint-disable-next-line no-unused-vars
  minTime: _minTime,
  // eslint-disable-next-line no-unused-vars
  maxTime: _maxTime
}: DateTimePickerProps) {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';

  // Normalize the value to handle both Date objects and strings
  const normalizedValue = React.useMemo(() => {
    if (!value) return null;
    if (value instanceof Date) return value;
    const parsed = new Date(value);
    return isNaN(parsed.getTime()) ? null : parsed;
  }, [value]);

  const handleChange = (date: Date | null) => {
    if (date) {
      onChange?.(date);
    } else {
      onChange?.(undefined);
    }
  };

  return (
    <div className={cn('space-y-2', className)}>
      {label && (
        <Label className={cn(required && "after:content-['*'] after:ml-0.5 after:text-red-500")}>
          {label}
        </Label>
      )}
      <MantineDateTimePicker
        value={normalizedValue}
        onChange={handleChange}
        placeholder={placeholder}
        disabled={disabled}
        minDate={fromDate}
        maxDate={toDate}
        valueFormat='MMM D, YYYY h:mm A'
        clearable
        firstDayOfWeek={0}
        dropdownType='modal'
        modalProps={{
          zIndex: 10000
        }}
        // Time picker configuration - use 12-hour format with AM/PM
        timePickerProps={{
          format: '12h',
          minutesStep: timeStep || 30
        }}
        styles={{
          input: {
            height: '40px',
            fontSize: '14px',
            borderRadius: '6px',
            borderColor: 'hsl(var(--input))',
            backgroundColor: 'hsl(var(--background))',
            color: 'hsl(var(--foreground))'
          },
          calendar: {
            backgroundColor: isDark ? 'hsl(var(--popover))' : 'hsl(var(--background))',
            color: isDark ? 'hsl(var(--popover-foreground))' : 'hsl(var(--foreground))',
            borderColor: 'hsl(var(--border))'
          },
          calendarHeader: {
            backgroundColor: isDark ? 'hsl(var(--popover))' : 'hsl(var(--background))',
            color: isDark ? 'hsl(var(--popover-foreground))' : 'hsl(var(--foreground))'
          },
          calendarHeaderControl: {
            color: isDark ? 'hsl(var(--popover-foreground))' : 'hsl(var(--foreground))',
            '&:hover': {
              backgroundColor: 'hsl(var(--accent))'
            }
          },
          calendarHeaderLevel: {
            color: isDark ? 'hsl(var(--popover-foreground))' : 'hsl(var(--foreground))',
            '&:hover': {
              backgroundColor: 'hsl(var(--accent))'
            }
          },
          weekday: {
            color: 'hsl(var(--muted-foreground))'
          },
          month: {
            backgroundColor: isDark ? 'hsl(var(--popover))' : 'hsl(var(--background))',
            color: isDark ? 'hsl(var(--popover-foreground))' : 'hsl(var(--foreground))'
          },
          year: {
            backgroundColor: isDark ? 'hsl(var(--popover))' : 'hsl(var(--background))',
            color: isDark ? 'hsl(var(--popover-foreground))' : 'hsl(var(--foreground))'
          }
        }}
      />
      {error && <p className='text-sm text-destructive'>{error}</p>}
    </div>
  );
}
